import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../../api/axiosConfig.jsx";
import { jwtDecode } from "jwt-decode";

const CourseList = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState({});
    const [courseCompletion, setCourseCompletion] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/courses');
            console.log('Fetched courses:', response.data);  // Log het antwoord om de structuur te controleren

            if (Array.isArray(response.data)) {
                setCourses(response.data);
                await checkCourseCompletion(response.data);
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (err) {
            console.error('Error fetching courses:', err);
            setError('Er is een fout opgetreden bij het ophalen van de cursussen.');
        } finally {
            setLoading(false);
        }
    };

    const checkCourseCompletion = async (courses) => {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) return;

        const decodedToken = jwtDecode(authToken);
        const userId = decodedToken?.userId;

        if (userId) {
            try {
                const progressPromises = courses.map(async (course) => {
                    // Haal de lessen op voor deze cursus
                    const lessonsResponse = await axiosInstance.get(`/lessons/course/${course.courseId}`);
                    const totalLessons = lessonsResponse.data.length;

                    // Haal de voortgang van de gebruiker voor deze cursus op
                    const progressResponse = await axiosInstance.get(`/progress/${userId}/${course.courseId}`);
                    const lastCompletedLesson = progressResponse.data.currentLessonId;

                    // Bereken de voltooiingspercentage en rond af naar boven
                    const completedLessons = lessonsResponse.data.filter(lesson => lesson.lessonId <= lastCompletedLesson).length;
                    let completionPercentage = (completedLessons / totalLessons) * 100;
                    completionPercentage = Math.ceil(completionPercentage); // Afronden naar boven

                    // Bepaal of de cursus voltooid is
                    const isCompleted = completionPercentage === 100;

                    return { courseId: course.courseId, completionPercentage, isCompleted };
                });

                const progressData = await Promise.all(progressPromises);
                const progressMap = progressData.reduce((acc, { courseId, completionPercentage, isCompleted }) => {
                    acc[courseId] = { completionPercentage, isCompleted };
                    return acc;
                }, {});

                setCourseCompletion(progressMap);
            } catch (err) {
                console.error('Error fetching progress or lessons:', err);
                setError('Er is een fout opgetreden bij het ophalen van de voortgang of lessen.');
            }
        }
    };

    const handleStartCourse = async (courseId) => {
        if (!courseId) {
            console.error('No course ID provided');  // Log als courseId niet bestaat
            setError('Ongeldige cursus ID');
            return;
        }

        try {
            const authToken = sessionStorage.getItem('authToken');
            if (!authToken) {
                setError('Je bent niet ingelogd. Log eerst in om een cursus te starten.');
                return;
            }

            const decodedToken = jwtDecode(authToken);
            const userId = decodedToken?.userId;

            if (!userId) {
                setError('Er is een probleem met je login. Log opnieuw in.');
                return;
            }

            // Check if progress already exists
            const progressCheckResponse = await axiosInstance.get(`/progress/check?userId=${userId}&courseId=${courseId}`);

            // Only create progress if it doesn't exist
            if (!progressCheckResponse.data.exists) {
                await axiosInstance.post('/progress', {
                    userId,
                    courseId,
                    currentLessonId: null
                });
            }

            // Navigate to the course
            navigate(`/courses/${courseId}`);
        } catch (err) {
            console.error('Error starting course:', err);
            setError(err.message || 'Er is een fout opgetreden bij het starten van de cursus.');
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Laden...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <h1 className="mb-4">Beschikbare Cursussen</h1>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {courses.length > 0 ? (
                <Row xs={1} md={2} lg={3} className="g-4">
                    {courses.map((course) => {
                        console.log('Course ID:', course.courseId);  // Log de course.courseId om zeker te zijn dat het bestaat
                        const { completionPercentage, isCompleted } = courseCompletion[course.courseId] || {};
                        return (
                            <Col key={course.courseId}>
                                <Card className="h-100 shadow-sm">
                                    {course.imageUrl && (
                                        <Card.Img
                                            variant="top"
                                            src={course.imageUrl}
                                            alt={course.title}
                                            style={{ height: '150px', objectFit: 'cover' }}
                                        />
                                    )}
                                    <Card.Body className="d-flex flex-column">
                                        <Card.Title>{course.title}</Card.Title>
                                        <Card.Text>{course.description}</Card.Text>
                                        <div className="mt-auto">
                                            <Button
                                                variant="primary"
                                                onClick={() => handleStartCourse(course.courseId)}
                                                className="w-100"
                                            >
                                                {isCompleted ? 'Voltooid' : 'Start Cursus'}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                    <Card.Footer className="text-muted">
                                        <small>
                                            {isCompleted
                                                ? `Voltooid (100%)`
                                                : `Voltooid: ${completionPercentage}%`}
                                        </small>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <Alert variant="info">
                    Geen cursussen beschikbaar op dit moment.
                </Alert>
            )}
        </Container>
    );
};

export default CourseList;
